import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queryKeys'
import { toast } from '@/shared/model/toast'
import type {
  Campaign,
  CampaignCreateRequest,
  CampaignTargetsResponse,
  TargetResult,
  TargetState,
} from '@/shared/api/types'

interface TargetsParams {
  state?: TargetState
  result?: TargetResult
  limit?: number
  offset?: number
}

function targetQuery(params: TargetsParams): string {
  const query = new URLSearchParams()
  if (params.state) query.set('state', params.state)
  if (params.result) query.set('result', params.result)
  query.set('limit', String(params.limit ?? 50))
  query.set('offset', String(params.offset ?? 0))
  return query.toString()
}

export const campaignsApi = {
  list: (signal?: AbortSignal) => api.get<Campaign[]>('/campaigns', signal),
  create: (body: CampaignCreateRequest) => api.post<Campaign>('/campaigns', body),
  details: (id: number, signal?: AbortSignal) => api.get<Campaign>(`/campaigns/${id}`, signal),
  targets: (id: number, params: TargetsParams, signal?: AbortSignal) =>
    api.get<CampaignTargetsResponse>(
      `/campaigns/${id}/targets?${targetQuery(params)}`,
      signal,
    ),
  control: (id: number, action: 'pause' | 'resume' | 'stop') =>
    api.post<Campaign>(`/campaigns/${id}/${action}`),
}

export function useCampaigns() {
  return useQuery({
    queryKey: queryKeys.campaigns,
    queryFn: ({ signal }) => campaignsApi.list(signal),
  })
}

export function useCampaign(id: number | null) {
  return useQuery({
    queryKey: [...queryKeys.campaign, id],
    queryFn: ({ signal }) => campaignsApi.details(id as number, signal),
    enabled: id !== null,
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 3000 : false),
  })
}

export function useCampaignTargets(id: number | null, params: TargetsParams, poll: boolean) {
  const query = targetQuery(params)
  return useQuery({
    queryKey: [...queryKeys.campaignTargets, id, query],
    queryFn: ({ signal }) => campaignsApi.targets(id as number, params, signal),
    enabled: id !== null,
    placeholderData: (previous) => previous,
    refetchInterval: poll ? 3000 : false,
  })
}

export function useCreateCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CampaignCreateRequest) => campaignsApi.create(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.campaigns })
      void queryClient.invalidateQueries({ queryKey: queryKeys.contacts })
      toast.success('Обзвон запущен')
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось запустить обзвон')
    },
  })
}

export function useCampaignControl(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (action: 'pause' | 'resume' | 'stop') => campaignsApi.control(id, action),
    onSuccess: (campaign) => {
      queryClient.setQueryData([...queryKeys.campaign, id], campaign)
      void queryClient.invalidateQueries({ queryKey: queryKeys.campaigns })
      void queryClient.invalidateQueries({ queryKey: queryKeys.campaignTargets })
      toast.success(
        campaign.status === 'paused'
          ? 'Обзвон на паузе'
          : campaign.status === 'running'
            ? 'Обзвон продолжен'
            : 'Обзвон остановлен',
      )
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось изменить обзвон')
    },
  })
}
